import { bearerAuthHeader, postObject, putObject, putVoid } from '../ChoiceApi'
import { NoTzDate } from '../DateUtils';
import { TwoFactorApplicationTypes, TwoFactorEnforcementType } from '../../Enums/ChoiceEnums'
import { IResult } from '../Models/Result';
import * as Notification from "../../lib/Notification"
import { ILoginSystemInfoModel } from '../Misc/SystemInfo';

//export async function authenticateUserNameAndPassword(request: ILoginRequestModel): Promise<IAuthRespModal | undefined>
export async function authenticateUserNameAndPassword(request: ILoginRequestModel): Promise<{jwt?:string, testModePassed: boolean} | undefined>
{
    return await postObject("api/User/Authentication/AuthenticateUserNameAndPassword", request);
}

// Confirm the user's identity message (legacy 2FA)
//  Return a JWT if confirmed, undefined if not confirmed
export async function sendLegacyToken(jwt?: string): Promise<IResult|undefined>
{
    return await postObject("api/User/Authentication/SendLegacyToken", undefined, 
        { 
            headers: bearerAuthHeader(jwt),
            errorCallback: handleAuthErrorMessage
        });
}


// Confirm the user's identity message (legacy 2FA)
//  Return a JWT if confirmed, undefined if not confirmed
export async function confirmUserIdentityToken(twoFactorCode: string, jwt?: string): Promise<JwtResp|undefined>
{
    return await postObject("api/User/Authentication/ConfirmUserIdentityToken", {twoFactorCode: twoFactorCode}, 
        { 
            headers: bearerAuthHeader(jwt),
            errorCallback: handleAuthErrorMessage
        });
}

// Confirm the user's two factor application code
//  Return a JWT if confirmed, undefined if not confirmed
export async function confirmUserTwoFactorAppCode(twoFactorCode: string, jwt?: string): Promise<JwtResp|undefined>
{
    return await postObject("api/User/Authentication/ConfirmUserTwoFactorAppCode", {twoFactorCode: twoFactorCode}, 
        { 
            headers: bearerAuthHeader(jwt),
            errorCallback: handleAuthErrorMessage
        });
}

export async function inspectCurrentPassword(password: string, jwt?: string): Promise<{passwordIssues?: string[], jwt?: string, minPasswordLen?: number}|undefined>
{
    return await postObject("api/User/Authentication/InspectCurrentPassword", { password },
    {
        headers: bearerAuthHeader(jwt),
        errorCallback: handleAuthErrorMessage
    });
}

// See if the user (from the jwt) is logged into the database
export async function checkForExistingUserSession(jwt?:string): Promise<{existingSession: boolean, jwt:string}|undefined>
{
    return await postObject("api/User/Authentication/CheckForExistingUserSession", undefined,
    {
        headers: bearerAuthHeader(jwt),
        errorCallback: handleAuthErrorMessage
    });    
}

export async function checkContracts(jwt?: string): Promise<{contracts: ILoginContractModel[], jwt: string} | undefined> 
{
    return await postObject("api/User/Authentication/CheckContracts", null,
    {
        headers: bearerAuthHeader(jwt),
        errorCallback: handleAuthErrorMessage
    });
}

export async function checkMessages(jwt?: string): Promise<{messages?: ILoginMessageItem[], jwt: string }|undefined>
{
    return await postObject("api/User/Authentication/CheckMessages", null,
    {
        headers: bearerAuthHeader(jwt),
        errorCallback: handleAuthErrorMessage
    });
}
export interface ILoginMessageItem
{
    userMessage?: string,
    globalUserMessageId?: number
}

export async function messageShown(globalUserMessageId: number, jwt?:string) : Promise<JwtResp|undefined>
{
    return await postObject(`api/User/Authentication/Message/${globalUserMessageId}`, null,
    {
        headers: bearerAuthHeader(jwt),
        errorCallback: handleAuthErrorMessage
    });
}

export async function acceptContract(contract_assign_id:number, userId: string, company: string, accepted: boolean, assignment_date: NoTzDate, confirmationValue: string, jwt?: string): Promise<boolean> 
{
    if (!userId)
    {
        alert("Invalid userId value");
        return false;
    }
    if (!company)
    {
        alert("Invalid company value");
        return false;
    }

    let result = true;
    const resp = await postObject(`api/User/Authentication/UpdateContract`, 
        {
            contractAssignId: contract_assign_id,
            userId,
            company,
            assignment_date,
            confirmationValue,
            accepted: true
        },
        {
            headers: bearerAuthHeader(jwt),
            errorCallback: handleAuthErrorMessage
        });
    return resp ? true : false; 
}

// Update the password
export async function updatePassword(oldPassword?: string, newPassword?: string, jwt?: string) : Promise<UpdatePasswordRespModel|undefined>
{
    const resp = await postObject<UpdatePasswordRespModel>(`api/User/Authentication/UpdatePassword`, {oldPassword, newPassword},
    {
        headers: bearerAuthHeader(jwt),
        errorCallback: handleAuthErrorMessage
    });
    return resp; 
}
interface UpdatePasswordRespModel { passwordIssues?: string[] };


// Get the authenticator application setup
export async function authenticatorSetup(encKey: string, appEntryText: string, jwt?: string): Promise<IAuthenicatorSetupRespModel | undefined>
{
    return postObject("api/User/Authentication/AuthenticatorSetup", {encKey: encKey, appEntryText},
    {
        headers: bearerAuthHeader(jwt),
        errorCallback: handleAuthErrorMessage
    });
}

export async function finalize(isEmergencyLogin: boolean, systemInfo: ILoginSystemInfoModel, jwt?:string): Promise<IResult|undefined>
{
    return postObject("api/User/Authentication/Finalize", { isEmergencyLogin, systemInfo },
    {
        headers: bearerAuthHeader(jwt),
        errorCallback: handleAuthErrorMessage
    });    
}

function handleAuthErrorMessage(httpResp: Response, errorMessage?: string): void
{
    if (401 === httpResp.status)
    {
        Notification.popAlert("Authentication problem -- your token may have expired");
        return;
    }
    if (401 === httpResp.status || 403 === httpResp.status)
    {
        Notification.popAlert("Authentication problem -- endpoint rejected call");        
        return;
    }

    alert(`Error: ${errorMessage ?? ""}`);
}


// Data used to log a user in
export interface ILoginRequestModel
{
    user_id: string,
    password: string,
    company: string,
    testMode?: string
}

// The response to a user login request
export interface IAuthRespModal
{
    authStatus: AuthenticationStatusTypes,
    messages?: string[],
    databaseKey?: string,
    twoFactorTokenType?: TwoFactorTypes,
    loginKey?: string,
    passwordExpired?: boolean,
    loginContracts?: ILoginContractAndStatusModel[],
    minimumPasswordLength?: number,
    twoFactorApplicationType?: TwoFactorApplicationTypes,
    twoFactorEnforcement?: TwoFactorEnforcementType,
    encryptedTwoFactorKey?: string,
    jwt?: string    // The JWT to provide info  
}

export interface ILoginContractModel
{
    contract_assign_id: number,
    assignment_date: NoTzDate,
    confirmationValue: string,
    contract_text?: string,
    description?: string,
    title_bar?: string,
    instructions?: string,
    rejection_message?: string
}
export interface IContractStatus
{
    contractRejected?: boolean
}

export type ILoginContractAndStatusModel = ILoginContractModel & IContractStatus;

export interface IAuthenicatorSetupRespModel
{ 
    account: string, 
    manualEntryKey: string, 
    qrCodeSetupImageUrl: string 
}


export enum AuthenticationStatusTypes
{
    Unauthorized = 1,
    LoginFailure = 2,
    LoginFailureWithMessage = 3,

    // User authenticated but action needed
    RequireTwoFactorToken = 10,
    BadNewPassword = 11,
    NeedToUpdatePassword = 12,
    UserLoggedInWithAnotherSession = 13,         // User logged into another session.

    // System status issue
    OpenLoginContact = 21,

    // We are fully logged in!
    Authenticated = 100
}

export enum TwoFactorTypes
{
    // None = "N",
    // Email = "E",
    // Text = "T",
    // TwoFactorApplication = "2"

    None = 1,
    Email = 2,
    Text = 3,
    TwoFactorApplication = 4

}

interface JwtResp{ jwt?: string }
